import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, inject, signal, viewChild } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Person } from '../models/person.model';
import { PersonEvent } from '../models/person-event.model';
import { PersonHeaderComponent } from './person-header.component';
import { buildPersonMinistryActivityLines } from '../pages/person-ministry-activity-lines';

@Component({
  selector: 'app-person-profile-sidebar',
  standalone: true,
  host: {
    '[style.--profile-panel-height]': "panelHeight() ? panelHeight() + 'px' : null",
    '[style.--profile-expanded-height]': "expandedHeight() + 'px'",
  },
  imports: [CommonModule, PersonHeaderComponent],
  template: `
    <aside #scrollViewport class="profile-sidebar" aria-label="Perfil del miembro" tabindex="0">
      <div class="profile-scroll-content"><div #panel class="profile-scroll-panel">
      <app-person-header
        layout="profile"
        [person]="person"
        [age]="age"
        [ministryActivityLines]="ministryActivityLines"
        [collapseProgress]="collapseProgress()"
        (statusChanged)="statusChanged.emit()"
        (homeAttendanceChanged)="homeAttendanceChanged.emit()">
      </app-person-header>
      </div></div>
    </aside>
  `,
  styles: [`
    :host { display: block; min-width: 0; min-height: 0; }
    .profile-sidebar { min-width: 0; height: 100%; overflow: auto; border-radius: 18px; }
    .profile-scroll-content, .profile-scroll-panel { height: 100%; }
    @media (max-width: 900px) {
      .profile-sidebar { height: var(--profile-panel-height, auto); overflow-x: hidden; overflow-y: auto; overscroll-behavior-y: contain; overflow-anchor: none; scrollbar-width: none; }
      .profile-sidebar::-webkit-scrollbar { display: none; }
      .profile-scroll-content { height: auto; min-height: calc(var(--profile-expanded-height) + 80px); padding-bottom: 80px; box-sizing: border-box; }
      .profile-scroll-panel { position: sticky; top: 0; height: auto; }
    }
  `],
})
export class PersonProfileSidebarComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) person!: Person;
  @Input() age?: number;
  @Input() events: PersonEvent[] = [];
  @Output() statusChanged = new EventEmitter<void>();
  @Output() homeAttendanceChanged = new EventEmitter<void>();
  readonly collapseProgress = signal(0);
  readonly panelHeight = signal(0);
  readonly expandedHeight = signal(0);
  private readonly panel = viewChild.required<ElementRef<HTMLElement>>('panel');
  private readonly scrollViewport = viewChild.required<ElementRef<HTMLElement>>('scrollViewport');
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private observer?: ResizeObserver;
  private frame?: number;
  private mobile?: MediaQueryList;
  private reducedMotion?: MediaQueryList;
  private scrollTop = 0;

  ngAfterViewInit() {
    const window = this.document.defaultView;
    if (!window) return;
    this.mobile = window.matchMedia('(max-width: 900px)');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.zone.runOutsideAngular(() => {
      this.panelHeight.set(this.panel().nativeElement.getBoundingClientRect().height);
      this.expandedHeight.set(this.panelHeight());
      this.observer = new ResizeObserver(() => {
        const height = this.panel().nativeElement.getBoundingClientRect().height;
        this.panelHeight.set(height);
        if (this.collapseProgress() === 0) this.expandedHeight.set(height);
      });
      this.observer.observe(this.panel().nativeElement);
      this.scrollViewport().nativeElement.addEventListener('scroll', this.onScroll, { passive: true });
      this.mobile!.addEventListener('change', this.onViewportChange);
    });
  }
  private readonly onViewportChange = () => {
    this.scrollViewport().nativeElement.scrollTop = 0;
    this.collapseProgress.set(0);
    this.panelHeight.set(this.panel().nativeElement.getBoundingClientRect().height);
    this.expandedHeight.set(this.panelHeight());
  };
  private readonly onScroll = (event: Event) => {
    if (!this.mobile?.matches || event.target !== this.scrollViewport().nativeElement) return;
    const viewport = this.scrollViewport().nativeElement;
    this.scrollTop = Math.min(80, viewport.scrollTop);
    if (viewport.scrollTop > 80) viewport.scrollTop = 80;
    const window = this.document.defaultView!;
    if (this.frame !== undefined) return;
    this.frame = window.requestAnimationFrame(() => {
      this.frame = undefined;
      const progress = Math.min(1, Math.max(0, this.scrollTop / 80));
      this.collapseProgress.set(this.reducedMotion?.matches ? Number(progress >= .5) : progress);
    });
  };
  ngOnDestroy() {
    this.observer?.disconnect(); this.scrollViewport().nativeElement.removeEventListener('scroll', this.onScroll);
    this.mobile?.removeEventListener('change', this.onViewportChange);
    if (this.frame !== undefined) this.document.defaultView?.cancelAnimationFrame(this.frame);
  }

  get ministryActivityLines() {
    return buildPersonMinistryActivityLines(this.person, this.events);
  }
}
